import type { ExportResult } from "@opentelemetry/core";
import type { IOtlpExportDelegate } from "@opentelemetry/otlp-exporter-base";
import { OTLPExporterBase } from "@opentelemetry/otlp-exporter-base";
import {
  convertLegacyBrowserHttpOptions,
  createOtlpSendBeaconExportDelegate,
  createOtlpXhrExportDelegate,
} from "@opentelemetry/otlp-exporter-base/browser-http";
import { JsonLogsSerializer } from "@opentelemetry/otlp-transformer";
import type { LogRecordExporter, ReadableLogRecord } from "@opentelemetry/sdk-logs";

type BrowserExporterConfig = {
  url?: string;
  timeoutMillis?: number;
};

export class HeadersInBodyOTLPLogExporter
  extends OTLPExporterBase<ReadableLogRecord[]>
  implements LogRecordExporter
{
  private readonly defaultDelegate: IOtlpExportDelegate<ReadableLogRecord[]>;
  private readonly beaconDelegate?: IOtlpExportDelegate<ReadableLogRecord[]>;
  private beaconModeUntil = 0;
  private visibilityListener?: () => void;
  private pageHideListener?: (event: Event) => void;
  private freezeListener?: () => void;
  private beforeUnloadListener?: (event: Event) => void;

  constructor(config: BrowserExporterConfig = {}, customHeaders?: Record<string, string>) {
    const serializer = {
      serializeRequest: (logs: ReadableLogRecord[]) => {
        const encoded = JsonLogsSerializer.serializeRequest(logs as unknown as ReadableLogRecord[]);
        if (!encoded) return encoded;
        try {
          const decoder = new TextDecoder();
          const body = JSON.parse(decoder.decode(encoded));
          const wrapped = { ...body, headers: customHeaders || {} };
          const encoderUtf8 = new TextEncoder();
          return encoderUtf8.encode(JSON.stringify(wrapped));
        } catch {
          return encoded;
        }
      },
      deserializeResponse: (data: Uint8Array) => JsonLogsSerializer.deserializeResponse(data),
    };

    const options = convertLegacyBrowserHttpOptions(config as any, "v1/logs", {
      "Content-Type": "application/json",
    });

    const defaultDelegate = createOtlpXhrExportDelegate(options, serializer as any);
    super(defaultDelegate as any);

    this.defaultDelegate = defaultDelegate as IOtlpExportDelegate<ReadableLogRecord[]>;
    this.beaconDelegate =
      typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
        ? (createOtlpSendBeaconExportDelegate(options, serializer as any) as IOtlpExportDelegate<
            ReadableLogRecord[]
          >)
        : undefined;

    this._setupBeaconGuards();
  }

  override export(logs: ReadableLogRecord[], resultCallback: (result: ExportResult) => void): void {
    this._getActiveDelegate().export(logs, resultCallback);
  }

  override forceFlush(): Promise<void> {
    return Promise.all([
      this.defaultDelegate.forceFlush(),
      this.beaconDelegate ? this.beaconDelegate.forceFlush() : Promise.resolve(),
    ]).then(() => void 0);
  }

  override async shutdown(): Promise<void> {
    this._teardownBeaconGuards();
    await Promise.all([
      this.defaultDelegate.shutdown(),
      this.beaconDelegate ? this.beaconDelegate.shutdown() : Promise.resolve(),
    ]);
  }

  public enterBeaconMode(durationMs: number = 2000) {
    if (!this.beaconDelegate) return;
    this.beaconModeUntil = Date.now() + durationMs;
  }

  private _setupBeaconGuards() {
    if (!this.beaconDelegate || typeof document === "undefined" || typeof window === "undefined") {
      return;
    }

    const markBeaconMode = () => {
      this.enterBeaconMode();
    };

    this.visibilityListener = () => {
      if (document.visibilityState === "hidden") {
        markBeaconMode();
      }
    };
    document.addEventListener("visibilitychange", this.visibilityListener, {
      capture: true,
      passive: true,
    } as any);

    this.pageHideListener = (event: Event) => {
      const pageEvent = event as PageTransitionEvent;
      if (!pageEvent.persisted) {
        markBeaconMode();
      }
    };
    window.addEventListener(
      "pagehide",
      this.pageHideListener as any,
      {
        capture: true,
        passive: true,
      } as any,
    );

    this.freezeListener = () => {
      markBeaconMode();
    };
    if ("onfreeze" in document) {
      document.addEventListener(
        "freeze",
        this.freezeListener as any,
        {
          capture: true,
          passive: true,
        } as any,
      );
    }

    this.beforeUnloadListener = () => {
      markBeaconMode();
    };
    window.addEventListener(
      "beforeunload",
      this.beforeUnloadListener as any,
      {
        capture: true,
      } as any,
    );
  }

  private _teardownBeaconGuards() {
    if (this.visibilityListener) {
      document.removeEventListener("visibilitychange", this.visibilityListener);
      this.visibilityListener = undefined;
    }
    if (this.pageHideListener && typeof window !== "undefined") {
      window.removeEventListener("pagehide", this.pageHideListener as any);
      this.pageHideListener = undefined;
    }
    if (this.freezeListener) {
      document.removeEventListener("freeze", this.freezeListener as any);
      this.freezeListener = undefined;
    }
    if (typeof window !== "undefined") {
      if (this.beforeUnloadListener) {
        window.removeEventListener("beforeunload", this.beforeUnloadListener as any);
        this.beforeUnloadListener = undefined;
      }
    }
  }

  private _getActiveDelegate(): IOtlpExportDelegate<ReadableLogRecord[]> {
    if (this.beaconDelegate && this._shouldUseBeacon()) {
      return this.beaconDelegate;
    }
    return this.defaultDelegate;
  }

  private _shouldUseBeacon() {
    if (!this.beaconDelegate) {
      return false;
    }
    if (this.beaconModeUntil && Date.now() < this.beaconModeUntil) {
      return true;
    }
    return false;
  }
}

export default HeadersInBodyOTLPLogExporter;
